import api from './api';

export interface IVideoEffect {
  effectId: string;
  type: string;
  enabled: boolean;
  params: Record<string, any>;
}

export interface IVideoSegment {
  segmentId: string;
  trackId: string;
  sourceType: 'source' | 'clip' | 'broll' | 'stock';
  sourceKey?: string;
  s3Url?: string;
  previewUrl?: string;
  title?: string;
  startTime: number;
  endTime: number;
  timelineStart: number;
  timelineEnd: number;
  layer: number;
  aspectRatio?: string;
  effects?: IVideoEffect[];
}

export interface IVideoTrack {
  trackId: string;
  type: 'video' | 'audio' | 'caption' | 'overlay';
  name: string;
  muted?: boolean;
  visible?: boolean;
  segments: IVideoSegment[];
}

export interface IVideoEditProject {
  projectId: string;
  title: string;
  sourceVideoS3Url?: string;
  sourceVideoS3Key?: string;
  previewUrl?: string;
  durationSeconds?: number;
  width?: number;
  height?: number;
  fps?: number;
  timeline: {
    tracks: IVideoTrack[];
  };
  status: 'draft' | 'processing' | 'rendering' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  outputS3Url?: string;
  outputS3Key?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StockSearchResult {
  id: string;
  title: string;
  previewUrl?: string;
  downloadUrl?: string;
  source: string;
  duration?: number;
}

export interface CreateProjectRequest {
  title: string;
  sourceVideoS3Url?: string;
  sourceVideoS3Key?: string;
  clipJobId?: string;
  clipId?: string;
  durationSeconds?: number;
  width?: number;
  height?: number;
  fps?: number;
  parentProjectId?: string;
}

const videoEditorApi = {
  createProject: (data: CreateProjectRequest) =>
    api.post<{ success: boolean; project: IVideoEditProject }>('/video-editor', data),

  uploadVideo: (file: File) => {
    const formData = new FormData();
    formData.append('video', file);
    return api.post<{ success: boolean; data: { s3Url: string; originalName: string } }>(
      '/video-editor/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  uploadAudio: (file: File) => {
    const formData = new FormData();
    formData.append('audio', file);
    return api.post<{ success: boolean; data: { s3Url: string; previewUrl: string; originalName: string } }>(
      '/video-editor/upload-audio',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post<{ success: boolean; data: { s3Url: string; previewUrl: string; originalName: string } }>(
      '/video-editor/upload-image',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  getProjects: () =>
    api.get<{ success: boolean; projects: IVideoEditProject[] }>('/video-editor'),

  getProject: (projectId: string) =>
    api.get<{ success: boolean; project: IVideoEditProject }>(`/video-editor/${projectId}`),

  updateTimeline: (projectId: string, timeline: { tracks: IVideoTrack[] }) =>
    api.put<{ success: boolean; project: IVideoEditProject }>(`/video-editor/${projectId}/timeline`, { timeline }),

  applyOperation: (projectId: string, type: string, params: Record<string, any>) =>
    api.post<{ success: boolean; operationId: string; status: string; outputS3Url?: string; error?: string }>(
      `/video-editor/${projectId}/operations`,
      { type, params }
    ),

  renderProject: (projectId: string) =>
    api.post<{ success: boolean; message: string }>(`/video-editor/${projectId}/render`),

  searchStockVideo: (q: string) =>
    api.get<{ success: boolean; results: StockSearchResult[] }>('/video-editor/stock/video', { params: { q } }),

  searchStockMusic: (q: string) =>
    api.get<{ success: boolean; results: StockSearchResult[] }>('/video-editor/stock/music', { params: { q } }),

  deleteProject: (projectId: string) =>
    api.delete<{ success: boolean; message: string }>(`/video-editor/${projectId}`),
};

export default videoEditorApi;
